import { useCallback, useContext, useMemo } from 'react';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { CellRenderer } from '@Components/StickyTable/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { BPMTableWidget } from '@Components/BPMTableWidget';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { OptimizationsContext, RunResultItem } from './optimizations-constants';

import './RunView.scss';

export const RunView = () => {
  const { runResult, runId, optimizations } = useContext(OptimizationsContext);

  const runInfo = useMemo(() => {
    if (!runId || !optimizations) {
      return undefined;
    }
    return R.filter(row => row.build_number === runId, optimizations)[0];
  }, [runId, optimizations]);

  const overviewData = useMemo(() => {
    if (!runId || !runInfo) {
      return [];
    }

    return [
      { key: '#', value: runId },
      { key: 'Status', value: runInfo.status },
      { key: 'Date', value: runInfo.date },
      { key: 'Constraint', value: runInfo.constraint_name },
      { key: 'Obj. Value', value: runInfo.objective_value },
      { key: 'Cost', value: runInfo.cost },
      { key: 'Budget', value: runInfo.budget }
    ];
  }, [runId, runInfo]);

  const overviewHeader = useMemo(
    () => [
      {
        label: 'Key',
        name: 'key',
        nonInteractive: true
      },
      {
        label: 'Value',
        name: 'value',
        nonInteractive: true,
        width: 200,
        renderer: (row, rowIndex) => {
          if (['Obj. Value', 'Objective Value'].includes(overviewData[rowIndex]?.key ?? '')) {
            return <NumberFormatter value={row.value} type='' decimals={1} />;
          }
          if (['Cost', 'Budget'].includes(overviewData[rowIndex]?.key ?? '')) {
            return <NumberFormatter value={parseFloat(row.value)} type='$' decimals={0} />;
          }
          if (['Date'].includes(overviewData[rowIndex]?.key ?? '')) {
            return R.pipe(Dfns.parseISO, Dfns.format('yyyy-MM-dd hh:mma'))(row.value);
          }
          return row.value;
        }
      }
    ],
    [overviewData]
  );

  const headers = useMemo(
    () => [
      {
        label: 'Feature',
        name: 'channel',
        flex: 2
      },
      {
        label: 'Kind',
        name: 'kind',
        width: 100
      },
      {
        label: 'Feature Spend',
        name: 'feature_spend',
        renderer: (row: RunResultItem) => {
          const value = row.feature_spend;
          const displayValue = Math.abs(value) < 0.005 ? 0 : value;
          return <NumberFormatter value={displayValue} type='$' decimals={2} />;
        },
        width: 200
      }
    ],
    []
  );

  // Filter out __TOTAL__ row if it exists
  const filteredRunResult = useMemo(() => {
    if (!runResult) {
      return [];
    }
    return runResult.filter(row => row.channel !== '__TOTAL__' && row.kind !== 'ALL');
  }, [runResult]);

  const totalSpend = useMemo(() => {
    if (!filteredRunResult || filteredRunResult.length === 0) {
      return 0;
    }
    return filteredRunResult.reduce((sum, item) => sum + (item.feature_spend ?? 0), 0);
  }, [filteredRunResult]);

  const totalsRow = useMemo(() => ({ feature: '', feature_spend: totalSpend }), [totalSpend]);

  const totalsRenderer: CellRenderer<Element | number | string | undefined> = ({
    data,
    style = {},
    classes = []
  }) => {
    let output;
    if (Number.isFinite(data) && (data as number) > 0) {
      output = <NumberFormatter value={data} type='$' decimals={2} />;
    } else {
      output = data;
    }
    return (
      <div style={style} className={[...classes, 'grand-total-cell'].join(' ')}>
        {output}
      </div>
    );
  };

  const renderResultTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={!runResult}
        emptyMessage={`No optimization results available for run ${runId}`}
        rowHeight={50}
        headerHeight={40}
      />
    ),
    [runResult, runId]
  );

  const renderOverviewTableLoading = useCallback(
    () => <TableLoading isLoading={!runResult} rowHeight={50} headerHeight={40} />,
    [runResult]
  );

  return (
    <div className='run-view'>
      <div className='result-pivot'>
        <div className='result-pivot-column middle'>
          <BPMTableWidget className='result-pivot-widget' tableClassName='result-pivot-table'>
            <BPMTable
              headerHeight={40}
              headers={headers}
              data={filteredRunResult}
              filterBar
              totals={totalsRow}
              totalsRenderer={totalsRenderer}
              noRowsRenderer={renderResultTableLoading}
            />
          </BPMTableWidget>
        </div>
        <div className='result-pivot-column right'>
          <BPMTableWidget className='overview-widget' tableClassName='overview-table'>
            <BPMTable
              headerHeight={40}
              headers={overviewHeader}
              data={overviewData}
              filterBar={false}
              noRowsRenderer={renderOverviewTableLoading}
            />
          </BPMTableWidget>
        </div>
      </div>
    </div>
  );
};
